/*
 * DJH search query normalizer.
 *
 * Shopify's storefront search matches ANY term in a multi-word query, so pasting
 * a full product title returns everything that shares a single word — searching
 * "Cushion 3/4 CTW Natural Diamond Semi-Set Engagement Ring" returned 351 results
 * (12+ pages) instead of the one ring. Joining the terms with AND narrows that to
 * products matching every word.
 *
 * Two entry points are covered: search form submits, and /search URLs reached any
 * other way (shared link, back button, Google sitelinks searchbox).
 *
 * AND can over-narrow, so a zero-result narrowed search falls through three stages
 * (see STAGE_PARAM). sections/search.liquid marks the zero-result page for us;
 * everything after that is decided here.
 */
(function () {
  if (window.djhSearchQuery) return;

  var MAX_TERMS = 10;

  /*
   * How far a zero-result search has been relaxed. Absent = still strict.
   *
   *   (absent)  every term ANDed         "honu AND pendant"  -> 0
   *   relaxed   category nouns dropped   "honu"              -> 4  ✓
   *   broad     Shopify's match-any      "honu pendant"      -> 24 unrelated pendants
   *
   * Relaxing before broadening matters: there are no honu pendants, so the honest
   * answer is the four honu pieces, not two dozen pendants with no honu on them.
   */
  var STAGE_PARAM = 'djh_stage';
  var ORIGINAL_PARAM = 'djh_q';

  // Low-signal words that only widen a jewelry catalog search.
  var STOPWORDS = [
    'a', 'an', 'and', 'are', 'as', 'at', 'be', 'but', 'by', 'for', 'from', 'he',
    'her', 'hers', 'him', 'his', 'how', 'in', 'is', 'it', 'its', 'me', 'my', 'of',
    'on', 'or', 'our', 'she', 'that', 'the', 'their', 'them', 'they', 'this', 'to',
    'was', 'what', 'when', 'where', 'which', 'who', 'why', 'with', 'you', 'your'
  ];

  /*
   * Product-category nouns. Only ever dropped from an already-zero-result search,
   * where the distinctive term ("honu", "maile") is what's worth keeping and the
   * category word is what made the query impossible.
   */
  var CATEGORY_WORDS = [
    'anklet', 'anklets', 'band', 'bands', 'bangle', 'bangles', 'bracelet',
    'bracelets', 'brooch', 'chain', 'chains', 'charm', 'charms', 'cuff', 'cuffs',
    'earring', 'earrings', 'hoop', 'hoops', 'jewelry', 'necklace', 'necklaces',
    'pendant', 'pendants', 'piece', 'pieces', 'ring', 'rings', 'set', 'sets',
    'stud', 'studs'
  ];

  /*
   * Hawaiian vocabulary — customer/HAWAIIAN-VOCABULARY.md §4, Henry-approved
   * 2026-08-08. Each group is bidirectional: searching ANY member expands to
   * every member, OR-joined, so `melia` finds products titled "Plumeria" and
   * vice versa. No Search & Discovery app needed — Shopify's native query
   * syntax already supports OR/parens/quotes with nothing configured
   * (confirmed against live data: `(melia OR plumeria)` correctly returns the
   * 17 plumeria pieces `melia` alone can't find, zero app installed).
   *
   * Expansion is purely additive (the searched term is always included in its
   * own OR-group), so it can only surface more correct results, never fewer —
   * safe to apply to every member uniformly, including common English anchors
   * like "queen" or "warrior" that carry a Hawaiian-figure group.
   *
   * `ohana`/`aloha` are deliberately absent — no product contains either word
   * in any form, so there's no synonym target to point at; both are instead
   * answered by the hand-curated block in djh-search-curated.liquid.
   */
  var SYNONYM_GROUPS = [
    ['honu', 'sea turtle', 'turtle', 'sea turtles', 'petroglyph turtle'],
    ['honu ea', "honu ʻea", 'hawksbill', 'hawksbill turtle'],
    ['naia', "naiʻa", 'dolphin', 'dolphins'],
    ['kohola', 'koholā', 'whale', 'humpback'],
    ['mano', 'manō', 'shark'],
    ['pueo', 'owl'],
    ['iwa', "ʻiwa", 'frigate bird'],
    ['moo', "moʻo", 'gecko', 'lizard'],
    ['maile', 'maile lei', 'lei maile'],
    ['hakulei', 'haku lei', 'lei haku'],
    ['melia', 'pua melia', 'plumeria', 'plumerias', 'frangipani'],
    ['lehua', 'ohia', "ʻōhiʻa", 'ohia lehua', "ʻōhiʻa lehua"],
    ['pua kenikeni', 'puakenikeni', 'puakenekene', 'kenikeni'],
    ['pikake', 'pīkake', 'jasmine'],
    ['ilima', "ʻilima"],
    ['lokelani', 'rose', 'roses', 'damask rose'],
    ['nanu', 'nānū', 'nau', "naʻu", 'gardenia', 'tiare'],
    ['lauae', "lauaʻe", 'palapalai', 'fern'],
    ['lai', "lāʻī", 'ti leaf', 'ti-leaf'],
    ['ulu', 'breadfruit'],
    ['kalo', 'taro'],
    ['hala', 'lauhala', 'pandanus'],
    ['awapuhi', "ʻawapuhi", 'ginger'],
    ['liliuokalani', "liliʻuokalani", 'liliokalani', 'queen liliuokalani', 'queen'],
    ['kalakaua', 'kalākaua', 'merrie monarch'],
    ['kaiulani', "kaʻiulani", 'princess kaiulani', 'princess'],
    ['kanaka maoli', 'native hawaiian'],
    ['ikaika', 'strong', 'warrior', 'mahiole', 'warrior helmet'],
    ['niho palaoa', 'whale tooth'],
    ['kahili', 'kāhili', 'feather standard'],
    ['kapa kuiki', 'kapa', 'quilt', 'hawaiian quilt', 'quilting'],
    ['kii pohaku', "kiʻi pōhaku", 'petroglyph', 'petroglyphs'],
    ['kuuipo', "kuʻu ipo", 'ipo', 'sweetheart', 'my love'],
    ['puanani', 'nani', 'kanani', 'beautiful'],
    ['makana', 'gift'],
    ['hoomau', "hoʻomau", 'forever'],
    ['keiki', 'child', 'children'],
    ['wahine', 'woman', 'kane', 'kāne', 'man'],
    ['aina', "ʻāina", 'land'],
    ['ukulele', "ʻukulele"],
    ['coat of arms', 'royal crest', 'hawaiian coat of arms'],
    ['scroll', 'scrollwork', 'engraved scroll'],
    ['barrel ring', 'barrel band'],
    ['cut out', 'cut-out', 'cutout', 'pierced', 'open work', 'openwork'],
    ['hand engraved', 'hand-engraved', 'engraved', 'engraving']
  ];

  // term (lowercase) -> its full group, built once at load. A multi-word group
  // member ("sea turtle") registers under its own lowercase string, matched
  // against multi-word tokens the same way single words are (see expandTerm).
  var SYNONYM_LOOKUP = {};
  SYNONYM_GROUPS.forEach(function (group) {
    group.forEach(function (term) {
      SYNONYM_LOOKUP[term.toLowerCase()] = group;
    });
  });

  function expandTerm(term) {
    var group = SYNONYM_LOOKUP[term.toLowerCase()];
    if (!group) return term;

    // THE SHOPPER'S OWN WORD LEADS THE GROUP, and that is a display decision as much
    // as a search one. search.liquid shows `or_alternatives[0]` in "N results for X" —
    // Liquid cannot read a separate URL param, so the first alternative IS the label.
    // Group order was authored for readability, so someone typing "plumeria" met a
    // page reading `17 results for "melia"`: a word they had not typed, for results
    // that were in fact theirs. `honu` and `maile` happened to lead their own groups
    // and looked fine, which is how this survived.
    //
    // Ordering here rather than adding a parameter keeps the search itself untouched:
    // OR is commutative, Shopify returns the same set, and the relaxation logic that
    // already uses djh_q for a different purpose is left exactly as it was.
    var typed = term.toLowerCase();
    var ordered = group.slice().sort(function (a, b) {
      return (b.toLowerCase() === typed ? 1 : 0) - (a.toLowerCase() === typed ? 1 : 0);
    });
    var alternatives = ordered.map(function (word) {
      return word.indexOf(' ') === -1 ? word : '"' + word + '"';
    });
    return '(' + alternatives.join(' OR ') + ')';
  }

  // Shopify search syntax the shopper typed deliberately — leave those queries alone.
  var EXPLICIT_SYNTAX = /(^|\s)(AND|OR|NOT)(\s|$)|["*:]/;

  function tokenize(query) {
    // Keep inner punctuation ("3/4", "Semi-Set") but drop wrapping commas/parens.
    return query
      .split(/\s+/)
      .map(function (token) {
        return token.replace(/^[^0-9a-z]+/i, '').replace(/[^0-9a-z]+$/i, '');
      })
      .filter(function (token) {
        return token && !/^[a-z]$/i.test(token);
      });
  }

  function normalize(raw) {
    var query = String(raw == null ? '' : raw).trim();
    if (!query || EXPLICIT_SYNTAX.test(query)) return query;

    // A query that IS, verbatim, a multi-word synonym member ("cut out", "coat
    // of arms", "sea turtle") — tokenize() below would split it into separate
    // AND'd words the lookup can never recognize apart, so a whole-phrase match
    // is checked first and expands as that one phrase instead.
    var wholeMatch = SYNONYM_LOOKUP[query.toLowerCase()];
    if (wholeMatch && query.indexOf(' ') !== -1) {
      return expandTerm(query);
    }

    var tokens = tokenize(query);
    var terms = tokens.filter(function (token) {
      return STOPWORDS.indexOf(token.toLowerCase()) === -1;
    });

    // A query of nothing but stopwords is better left as the shopper typed it.
    if (!terms.length) terms = tokens;
    if (!terms.length) return query;

    // "gifts for her" carries one real term — AND would only add noise.
    if (terms.length === 1) return expandTerm(terms[0]);

    return terms.slice(0, MAX_TERMS).map(expandTerm).join(' AND ');
  }

  function go(params) {
    window.location.replace(window.location.pathname + '?' + params.toString());
  }

  /*
   * "Search within a collection" — the picker's value rides inside `q` itself as
   * a trailing `AND tag:<handle>` term (see djh-search-scope-data.liquid's doc
   * comment for why: Liquid has no generic access to a separate URL param, only
   * to search.terms, so the scope has to travel in the one string it can read).
   * Every collection tag was confirmed 2026-08-08 to exactly match that
   * collection's live product count.
   */
  function foldScope(freeText, scopeSelect) {
    if (!scopeSelect || !scopeSelect.value) return freeText;
    var tagTerm = 'tag:' + scopeSelect.value;
    return freeText ? freeText + ' AND ' + tagTerm : tagTerm;
  }

  function bindForms() {
    var forms = document.querySelectorAll('form[role="search"]');

    Array.prototype.forEach.call(forms, function (form) {
      // Observable binding — see search-normalizer.spec.ts. Kept to one line:
      // this file has ~200 bytes before inline_asset_content drops it entirely.
      form.setAttribute('data-djh-search-bound', '');

      form.addEventListener('submit', function (event) {
        var input = form.querySelector('input[name="q"]');
        if (!input) return;

        var scopeSelect = form.querySelector('[data-djh-search-scope]');
        var rawFreeText = input.value.trim();

        // An empty query with a scope picked isn't really a search — it's "take
        // me to this collection." Send them to the real collection page (its own
        // filters/sort/pagination) instead of a bare, query-less results page.
        if (!rawFreeText && scopeSelect && scopeSelect.value) {
          var selectedOption = scopeSelect.options[scopeSelect.selectedIndex];
          var handle = selectedOption && selectedOption.getAttribute('data-handle');
          if (handle) {
            event.preventDefault();
            window.location.assign('/collections/' + handle);
            return;
          }
        }

        var narrowed = normalize(input.value);
        var withScope = foldScope(narrowed, scopeSelect);
        if (withScope === input.value.trim()) return;

        // Navigate manually so the shopper never sees "a AND b" (or "AND tag:x")
        // in the input.
        event.preventDefault();
        var url = new URL(form.getAttribute('action') || '/search', window.location.origin);
        new FormData(form).forEach(function (value, key) {
          if (key !== 'q' && key !== STAGE_PARAM && key !== ORIGINAL_PARAM && key !== 'collection_scope') {
            url.searchParams.append(key, value);
          }
        });
        url.searchParams.set('q', withScope);
        window.location.assign(url.toString());
      });
    });
  }

  /*
   * Which stage we're on is only knowable from the URL, so the explanatory copy and
   * the type filters are wired up here rather than in Liquid.
   */
  function markStage() {
    var params = new URLSearchParams(window.location.search);
    var stage = params.get(STAGE_PARAM);
    if (!stage) return;

    var notice = document.querySelector('[data-djh-search-' + stage + ']');
    if (notice) notice.hidden = false;

    // Keep the shopper on the same stage when they switch result type.
    var filters = document.querySelectorAll('[data-djh-search-filter]');
    Array.prototype.forEach.call(filters, function (link) {
      link.href += (link.href.indexOf('?') === -1 ? '?' : '&') + STAGE_PARAM + '=' + stage;
    });
  }

  /*
   * Pulls the `tag:x` scope segment (if any) out of an AND-split term array.
   * Everything downstream — relaxing, broadening, the "is this even a
   * different search" check — operates on free-text terms only. Without this,
   * the broad stage's plain-space rejoin (`terms.join(' ')`, needed so Shopify
   * treats the free text as match-any) would swallow "tag:x" into the middle
   * of that space-joined string instead of keeping it a distinct AND'd
   * segment — confirmed 2026-08-08: it still happened to scope the *query*
   * correctly (Shopify reads a bare `tag:` token as a hard filter regardless
   * of surrounding spaces/AND), but corrupted the *label* lookup, since
   * sections/search.liquid parses the scope back out by splitting on ' AND '.
   */
  function extractScope(terms) {
    var scopeTerm = null;
    var rest = [];
    terms.forEach(function (term) {
      if (/^tag:/.test(term)) {
        scopeTerm = term;
      } else {
        rest.push(term);
      }
    });
    return { scopeTerm: scopeTerm, rest: rest };
  }

  function reattachScope(freeText, scopeTerm) {
    if (!scopeTerm) return freeText;
    return freeText ? freeText + ' AND ' + scopeTerm : scopeTerm;
  }

  /*
   * A narrowed search that found nothing. Relax it one step rather than dumping the
   * shopper on an empty page. sections/search.liquid renders the marker element.
   */
  function handleZeroResults() {
    if (!document.querySelector('[data-djh-search-zero]')) return;

    var params = new URLSearchParams(window.location.search);
    var stage = params.get(STAGE_PARAM);
    if (stage === 'broad') return; // already as wide as Shopify goes

    var query = params.get('q') || '';
    var extracted = extractScope(query.split(' AND ').filter(Boolean));
    var scopeTerm = extracted.scopeTerm;
    var terms = extracted.rest; // free text only — scope never enters this math

    var originalParam = params.get(ORIGINAL_PARAM);
    var original = originalParam !== null ? originalParam : terms.join(' ');

    if (stage !== 'relaxed' && terms.length > 1) {
      var kept = terms.filter(function (term) {
        return CATEGORY_WORDS.indexOf(term.toLowerCase()) === -1;
      });
      if (kept.length && kept.length < terms.length) {
        params.set('q', reattachScope(kept.join(' AND '), scopeTerm));
        params.set(STAGE_PARAM, 'relaxed');
        params.set(ORIGINAL_PARAM, original);
        return go(params);
      }
    }

    // Nothing left to relax. Broaden only if the free text is a different search
    // than the one that just failed — otherwise this is a genuine no-results
    // page (scope alone never makes it "different"; it's constant throughout).
    if (original === terms.join(' ') && !stage) return;

    params.set('q', reattachScope(original, scopeTerm));
    params.set(STAGE_PARAM, 'broad');
    params.delete(ORIGINAL_PARAM);
    go(params);
  }

  // Runs at parse time (the script is inlined, not deferred) so a broad /search URL
  // is replaced before the results below it render.
  function normalizeLocation() {
    if (window.location.pathname.indexOf('/search') === -1) return;

    var params = new URLSearchParams(window.location.search);
    if (params.get(STAGE_PARAM)) return;

    var current = params.get('q');
    if (!current) return;

    var narrowed = normalize(current);
    if (narrowed === current) return;

    params.set('q', narrowed);
    go(params);
  }

  function onReady() {
    bindForms();
    markStage();
    handleZeroResults();
  }

  /**
   * The synonym group of a WHOLE query ("melia", "pua melia"), lowercased, or null. For the type-ahead, which cannot
   * send an OR-group (it ANDs every word) and so asks for each member on its own; a query of several concepts
   * ("melia ring") gets null and is sent as typed.
   */
  function synonymsOf(raw) {
    var key = String(raw == null ? '' : raw).trim().toLowerCase().replace(/\s+/g, ' ');
    var group = SYNONYM_LOOKUP[key];
    return group ? group.map(function (w) { return w.toLowerCase(); }) : null;
  }

  window.djhSearchQuery = { normalize: normalize, stageParam: STAGE_PARAM, synonymsOf: synonymsOf };

  normalizeLocation();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }
})();
